import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { type LanguageLevel } from '../types';
import { supabase } from '../utils/supabase';
import { useTranslation } from '../utils/translations';

interface LevelSelectionProps {
  onSelect: (level: LanguageLevel) => void;
  defaultLevel?: LanguageLevel;
  nativeLanguage?: string;
}

const levels: { level: LanguageLevel; description: string }[] = [
  { level: 'A1', description: 'Beginner - Basic phrases and expressions' },
  { level: 'A2', description: 'Elementary - Simple conversations' },
  { level: 'B1', description: 'Intermediate - Express opinions' },
  { level: 'B2', description: 'Upper Intermediate - Complex topics' },
  { level: 'C1', description: 'Advanced - Fluent expression' }
];

const customLevel = { level: 'Custom' as LanguageLevel, description: 'Your personal AI-generated lessons' };

export function LevelSelection({ onSelect, defaultLevel, nativeLanguage }: LevelSelectionProps) {
  const [currentLevel, setCurrentLevel] = useState<LanguageLevel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t, isRTL } = useTranslation(nativeLanguage || 'English');

  useEffect(() => {
    setIsLoading(true);
    
    async function loadUserLevel() {
      try {
        console.log('Loading user level with defaultLevel:', defaultLevel);
        
        // First check if we have a defaultLevel from props
        if (defaultLevel && ['A1', 'A2', 'B1', 'B2', 'C1', 'Custom'].includes(defaultLevel)) {
          console.log('Using defaultLevel from props:', defaultLevel);
          setCurrentLevel(defaultLevel as LanguageLevel);
          localStorage.setItem('currentLevel', defaultLevel);
          setIsLoading(false);
          return;
        }
        
        // Next check if we have a level in localStorage
        const savedLevel = localStorage.getItem('currentLevel');
        console.log('Saved level from localStorage:', savedLevel);
        
        if (savedLevel && ['A1', 'A2', 'B1', 'B2', 'C1', 'Custom'].includes(savedLevel)) {
          console.log('Using level from localStorage:', savedLevel);
          setCurrentLevel(savedLevel as LanguageLevel);
          setIsLoading(false);
          return;
        }
        
        // If no level is found, try to get it from the user's profile
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          console.log('Getting level from user profile');
          
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('current_level')
            .eq('user_id', session.user.id)
            .maybeSingle();
            
          if (profile && profile.current_level && ['A1', 'A2', 'B1', 'B2', 'C1', 'Custom'].includes(profile.current_level)) {
            console.log('Using level from profile:', profile.current_level);
            
            setCurrentLevel(profile.current_level as LanguageLevel);
            localStorage.setItem('currentLevel', profile.current_level);
            setIsLoading(false);
            return;
          } else {
            console.log('No valid level found in profile or error:', error);
          }
        }
        
        // Default to A1 if no level is found
        console.log('Defaulting to A1');
        setCurrentLevel('A1');
        localStorage.setItem('currentLevel', 'A1');
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading user level:', error);
        setCurrentLevel('A1');
        localStorage.setItem('currentLevel', 'A1');
        setIsLoading(false);
      }
    }
    
    loadUserLevel();
  }, [defaultLevel]);

  const handleSelectLevel = (level: LanguageLevel) => {
    console.log('Level selected:', level);
    setCurrentLevel(level);
    localStorage.setItem('currentLevel', level);
    onSelect(level);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-3xl mx-auto">
        <h2 className="text-center text-3xl font-extrabold text-gray-900 mb-8">
          {t('Select Your Level')}
        </h2>
        
        {isLoading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}
        
        {!isLoading && currentLevel && (
          <div className="text-center text-gray-600 mb-6">
            <p>Your current level: {currentLevel}</p>
          </div>
        )}
        
        {!isLoading && (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {levels.map(({ level, description }) => (
                <button
                  key={level}
                  onClick={() => handleSelectLevel(level)}
                  className={`p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-left ${
                    level === currentLevel
                      ? 'bg-blue-50 border-2 border-blue-500 shadow-md'
                      : 'bg-white'
                  }`}
                >
                  <h3 className="text-lg font-semibold text-gray-900">{level}</h3>
                  <p className="mt-2 text-sm text-gray-600">{t(description)}</p>
                </button>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-700 mb-4 text-center">
                {t('Or create your own')}
              </h3>
              <button
                onClick={() => handleSelectLevel(customLevel.level)}
                className={`w-full p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-left bg-gradient-to-r from-amber-50 to-orange-50 border-2 ${
                  currentLevel === 'Custom'
                    ? 'border-amber-500'
                    : 'border-amber-200 hover:border-amber-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Sparkles className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('Custom Lessons')}</h3>
                    <p className="mt-1 text-sm text-gray-600">{t(customLevel.description)}</p>
                  </div>
                </div>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}